import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { isProfileMissing, profileMissingTarget } from '../../utils/profileMissing';

/**
 * Guard for `/signup/payment` and `/signup/complete`.
 *
 * Deliberately not `ProtectedRoute`: that one sends an organization awaiting payment *to* these
 * pages, so wrapping them in it would loop. This only requires a signed-in user whose RosterPilot
 * user is not confirmed missing, and sends a signed-out visitor to sign-in with a return path, so a
 * coach whose session lapsed while on Stripe comes back to the page Stripe sent them to.
 */
export const SignupSessionRoute = ({ children }: { children: ReactNode }) => {
  const { user, initializing } = useAuth();
  const location = useLocation();

  if (initializing) {
    return null;
  }

  if (!user) {
    return (
      <Navigate
        to="/login"
        replace
        state={{ from: `${location.pathname}${location.search}` }}
      />
    );
  }

  // A session with no RosterPilot user has no signup status to show. An unfinished signup goes back
  // to its signup page to resume; anyone else to /login, which signs them out and explains. No return path:
  // coming back here would only repeat this.
  if (isProfileMissing(user)) {
    return <Navigate to={profileMissingTarget(user)} replace />;
  }

  return <>{children}</>;
};
